import React, { useState } from "react";
import "./ProjectPage.css";
import sociocracy from "../assets/images/aboutTheFarm/organizational-structure.jpg";
import thirteenPrinciples from "../assets/images/aboutTheFarm/thirteen-principles.png";
import { useTeamMembers } from "../hooks/useTeamMembers";
import { useStaffRoles } from "../hooks/useStaffRoles";
import { useAgroecologyResourceLinks } from "../hooks/useAgroecologyResourceLinks";
import { StaffRole, TeamMember, type GalleryMedia } from "../api/contentful";
import TeamSection from "../components/TeamSection";
import Hero from "../components/Hero";
import GalleryLightbox from "../components/GalleryLightbox";
import OUR_STORY1 from "../assets/images/aboutTheFarm/our-story1.avif";
import OUR_STORY2 from "../assets/images/aboutTheFarm/our-story2.avif";
import GROWING_METHOD_1 from "../assets/images/aboutTheFarm/growinMethods/growingMethod.avif";
import GROWING_METHOD_2 from "../assets/images/aboutTheFarm/growinMethods/growingMethodSecond.avif";
import GROWING_METHOD_3 from "../assets/images/aboutTheFarm/growinMethods/growingMethodThird.avif";
import GROWING_METHOD_4 from "../assets/images/aboutTheFarm/growinMethods/growing-methods-new.jpg";
import { NavLink } from "react-router-dom";

function SociocracyReadMore({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="sociocracy-read-more"
      onClick={onToggle}
      aria-expanded={expanded}
    >
      {expanded ? "Read less" : "Read more"}
    </button>
  );
}

function SociocracyRoleCard({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`sociocracy-role-card${expanded ? " is-expanded" : ""}${className ? ` ${className}` : ""}`}
    >
      <div className="sociocracy-role-card__header">
        <h4>{title}</h4>
        <SociocracyReadMore
          expanded={expanded}
          onToggle={() => setExpanded((prev) => !prev)}
        />
      </div>
      <div
        className={
          expanded
            ? "sociocracy-role-card__body is-expanded"
            : "sociocracy-role-card__body"
        }
      >
        {children}
      </div>
    </div>
  );
}

const THIRTEEN_PRINCIPLES_MEDIA: GalleryMedia[] = [
  { type: "image", url: thirteenPrinciples },
];

const ProjectPage: React.FC = () => {
  const [principlesLightboxOpen, setPrinciplesLightboxOpen] = useState(false);
  const {
    data: staffRoles,
    loading: staffRolesLoading,
    error: staffRolesError,
  } = useStaffRoles();

  const {
    data: teamMembers,
    loading: teamMembersLoading,
    error: teamMembersError,
  } = useTeamMembers();

  const { data: agroecologyResources } = useAgroecologyResourceLinks();

  const isLoadingStaffSection = staffRolesLoading || teamMembersLoading;
  const staffSectionError = staffRolesError || teamMembersError;

  const teamMembersByRoleId = new Map<string, TeamMember[]>();

  if (teamMembers && Array.isArray(teamMembers)) {
    for (const member of teamMembers) {
      const roles = Array.isArray(member.role)
        ? (member.role as StaffRole[])
        : [];
      for (const role of roles) {
        const roleId = role?.contentfulId;
        if (!roleId) continue;

        const existing = teamMembersByRoleId.get(roleId) || [];
        existing.push(member);
        teamMembersByRoleId.set(roleId, existing);
      }
    }
  }

  return (
    <>
      <Hero page="project" titleTwo="About The Project" />
      <div className="content-container project-page-container">
        <section className="our-story">
          <div className=" info-card project-page-card">
            <div className="project-page-card-img">
              <img src={OUR_STORY1} alt="Alnarp farm members" />
            </div>
            <div className="project-page-card-info">
              <h3>Our Story</h3>
              <p>
                <span className="text-yellow">Alnarp’s Agroecology Farm</span>{" "}
                was started in 2021 by a group of passionate Agroecology
                master’s students at SLU. They shared{" "}
                <span className="text-yellow">
                  a vision of creating a space for developing practical skills
                  in farming
                </span>
                , where theory could be tested by applying it to the reality of
                running a small-scale farming business. The farm would also{" "}
                <span className="text-yellow">
                  become a vital educational tool and research facility
                </span>{" "}
                over time, as well as a warm and welcoming environment that
                brought together{" "}
                <span className="text-yellow">
                  all members of the local community.
                </span>{" "}
              </p>

              <p>
                A project such as this was felt to be missing in Alnarp and so,
                <span className="text-yellow">
                  {" "}
                  encouraged by Faculty members and fellow students, the
                  agroecology students
                </span>{" "}
                drew up their vision, goals and business plan for Alnarp’s Farm.
                They were successful in obtaining initial grants and our first
                plot of land of 400m2 in front of Grohuset.
              </p>
            </div>
          </div>
          <div className=" info-card project-page-card project-page-card-reversed">
            <div className="project-page-card-img">
              <img src={OUR_STORY2} alt="Alnarp farm members" />
            </div>
            <div className="project-page-card-info">
              {/* <h3>Our Story</h3> */}
              <p>
                Due to lack of available space on the Alnarp campus, the project
                was offered land to{" "}
                <span className="text-yellow">
                  expand on at Mellangård in 2022
                </span>
                . By the end of 2022, Alnarp’s Farm will achieve financial
                self-sufficiency and continue to be handed down the{" "}
                <span className="text-yellow">
                  generations of students, researchers and committed individuals
                </span>{" "}
                who wish to learn, participate in and work towards a more
                sustainable food system.
              </p>

              <p>
                Since 2024 the project has
                <span className="text-yellow"> expanded even further</span>, and
                we are proud to say that currently we are using up to{" "}
                <span className="text-yellow">2000 m2 for market garden</span>{" "}
                production, as well as using{" "}
                <span className="text-yellow">
                  2000 m2 for our apple orchard
                </span>
                . Furthermore, the project has now hired 4{" "}
                <span className="text-yellow">part-time employees</span> to
                ensure the <span className="text-yellow">longevity</span> of the
                project and keep Alnarp's farm available to all those (students
                and non-students) who want to learn more about agroecology, or
                buy as much local vegetables as possible!
              </p>
            </div>
          </div>
        </section>
        <section className="growing-methods">
          <div className=" info-card project-page-card project-page-card-images-container">
            <div className="project-page-card-img">
              <img src={GROWING_METHOD_1} alt="Alnarp farm members" />
            </div>
            <div className="project-page-card-img">
              <img src={GROWING_METHOD_2} alt="Alnarp farm members" />
            </div>
            <div className="project-page-card-img">
              <img src={GROWING_METHOD_3} alt="Alnarp farm members" />
            </div>
            <div className="project-page-card-img">
              <img src={GROWING_METHOD_4} alt="Alnarp farm members" />
            </div>
          </div>
          <div className=" info-card project-page-card">
            <div className="project-page-card-vertical-info">
              <h3>Our growing method(s)</h3>
              <p>
                Our market garden is built on a{" "}
                <span className="text-yellow">‘no dig’ method</span> that is
                popular amongst small-scale vegetable producers. We have chosen
                this method because it{" "}
                <span className="text-yellow">minimises disturbance</span> to
                the soil and allows the
                <span className="text-yellow">
                  {" "}
                  soil microorganisms to flourish
                </span>
                . It also naturally reduces pressure from weeds, which means
                that chemical intervention is not necessary.
              </p>
              <p>
                Part of our garden is organically certified, although our
                methods are far{" "}
                <span className="text-yellow">
                  beyond the requirements for organic certification
                </span>
                .{" "}
              </p>
              <p>
                Since 2023, we have started to incorporate more perennial
                species and trees. This allows us to experiment with{" "}
                <span className="text-yellow">agroforestry </span>
                principles. Agroforestry seeks{" "}
                <span className="text-yellow">
                  harmonies between different species{" "}
                </span>
                and their abilities to defend each other from pests and disease,
                as well as to provide each other with nutrients and shelter from
                bad weather.
              </p>{" "}
              <p className="project-page-charles-section">
                Learn more from{" "}
                <a
                  href="https://www.charlesdowding.co.uk/"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="bold project-page-link-in-text"
                >
                  Charles Dowding
                </a>
                , the ultimate expert of the no dig method! And don't miss
                checking out the{" "}
                <a
                  href="https://themarketgardener.com/ "
                  className="bold project-page-link-in-text"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Market Garden Institute
                </a>{" "}
                for more information about organic farming and market gardening.
              </p>
            </div>
          </div>
        </section>
        <section className="agroecology">
          <div className=" info-card project-page-card">
            <div className="project-page-card-info">
              <h3>what is agroecology?</h3>

              <p>
                Agroecology is a{" "}
                <span className="text-yellow">holistic study </span> of the
                agricultural system , where the effects of growing food is
                analysed from{" "}
                <span className="text-yellow">
                  environmental, economic and social standpoints
                </span>
                .
              </p>

              <ul>
                Environmentally, the challenge is:
                <li className="environmental-challenges-list">
                  {" "}
                  How can we{" "}
                  <span className="text-yellow">
                    minimise the negative effects{" "}
                  </span>{" "}
                  of agriculture , such as{" "}
                  <span className="text-yellow">
                    pollution, biodiversity decline and soil depletion
                  </span>{" "}
                  while still being able to provide enough food? And then,
                </li>
                <li className="environmental-challenges-list">
                  How can we make it{" "}
                  <span className="text-yellow">economically viable</span>?
                </li>
                <li className="environmental-challenges-list">
                  Finally, does the food system reflect the needs of our{" "}
                  <span className="text-yellow">diverse society</span> or does
                  it serve the interests of just a few?
                </li>
              </ul>

              <p>
                Agroecology tackles the{" "}
                <span className="text-yellow">food system as a whole</span> by
                trying to find the best answers to these kinds of questions. Our
                little farm won’t change the whole food system, but we attempt
                to answer these questions through the{" "}
                <span className="text-yellow">daily decisions</span> we make.
              </p>

              <section className="agroecology-resourses">
                <h4>Agroecology resources:</h4>

                <ul className="agroecology-resources-list">
                  {(agroecologyResources ?? []).map((resource) => (
                    <li key={resource.contentfulId}>
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="agroecology-link"
                      >
                        {resource.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
            <div className="thirteen-principles-image-container">
              <button
                type="button"
                className="thirteen-principles-image-button"
                onClick={() => setPrinciplesLightboxOpen(true)}
                aria-label="View the 13 principles of sustainable food systems full size"
              >
                <img
                  src={thirteenPrinciples}
                  alt="The 13 principles of sustainable food systems."
                />
              </button>
              <div className="principles-of-agroecology">
                <p>
                  Overview of the{" "}
                  <span className="text-yellow">thirteen principles</span> of
                  agroecology and three operational principles of sustainable
                  food systems.{" "}
                  <a href="https://www.agroecology-europe.org/the-13-principles-of-agroecology/">
                    <span className="project-page-link-in-text">
                      Read more here!
                    </span>
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="sociocracy-section">
          <div className="info-card project-page-card sociocracy-card">
            <div className="sociocracy-intro-container">
              <header className="sociocracy-intro">
                <h3>Sociocracy Model</h3>
                <p>
                  Currently we are using a decision making method called{" "}
                  <span className="text-yellow">sociocracy</span>. Find out more
                  about the concept{" "}
                  <a
                    href="https://www.sociocracyforall.org/sociocracy/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="project-page-link-in-text"
                  >
                    here.
                  </a>
                </p>
              </header>
            </div>
            <div className="sociocracy-roles">
              <SociocracyRoleCard
                title="Board Members"
                className="sociocracy-role-card--board"
              >
                <p>
                  Board Members take ownership of their tasks and work
                  proactively, showing initiative to ensure projects and
                  responsibilities are successfully managed.
                </p>
                <ul>
                  <li className="sociocracy-list-item">
                    Time on the farm: Minimum 1 day per week (4 days/month)
                  </li>
                  <li className="sociocracy-list-item">
                    Administrative work: Flexible, depending on the area
                  </li>
                  <li className="sociocracy-list-item">
                    Meetings: Actively participate in board meetings (~1
                    hour/week)
                  </li>
                </ul>
                <p>
                  This role carries more commitment and responsibility than a
                  team member, but less than paid positions.
                </p>
              </SociocracyRoleCard>
              <SociocracyRoleCard
                title="Employees"
                className="sociocracy-role-card--employees"
              >
                <p>
                  Employees hold the highest level of commitment within the
                  organization, typically working 4–5 days per week, depending
                  on the season. Each employee is responsible for one of the
                  organization’s five areas, which includes both administrative
                  duties in the office and participation in farm working
                  sessions.
                </p>
              </SociocracyRoleCard>
              <SociocracyRoleCard title="Team Members">
                <p>
                  A Team Member takes on project-based roles with high
                  flexibility and no fixed schedule. While the time you dedicate
                  depends on the project or area you work in, you are expected
                  to take responsibility for completing any project you start,
                  especially if it impacts the team or overall project outcomes.{" "}
                  <span className="text-yellow">
                    Lower commitment than a board member
                  </span>
                  , but more responsibility than a custodian.
                </p>
              </SociocracyRoleCard>

              <SociocracyRoleCard
                title="Volunteers"
                className="sociocracy-role-card--volunteers"
              >
                <p>
                  As a volunteer, you have the freedom to{" "}
                  <span className="text-yellow">
                    get involved on your own terms
                  </span>
                  . You can choose when and how often you participate. This is a
                  great way to contribute your skills, meet new people, and be
                  part of our community in a way that fits your life.{" "}
                  <NavLink to="/join" className="project-page-link-in-text">
                    Read more about volunteering here!
                  </NavLink>
                </p>
              </SociocracyRoleCard>

              <figure className="sociocracy-diagram">
                <img
                  src={sociocracy}
                  alt="Organizational structure diagram for sociocracy at Alnarp's Agroecology Farm"
                />
              </figure>
            </div>
          </div>
        </section>

        <div className="info-card project-page-card team-section">
          <TeamSection
            isLoadingStaffSection={isLoadingStaffSection}
            staffSectionError={staffSectionError}
            staffRoles={staffRoles}
            teamMembersByRoleId={teamMembersByRoleId}
          />
        </div>
      </div>

      <GalleryLightbox
        isOpen={principlesLightboxOpen}
        media={THIRTEEN_PRINCIPLES_MEDIA}
        index={0}
        sectionTitle="13 principles of agroecology"
        onClose={() => setPrinciplesLightboxOpen(false)}
        onIndexChange={() => {}}
      />
    </>
  );
};

export default ProjectPage;
